import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Popover, Space, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { Machine } from '@/types'
import { formatHm, formatYMD } from '@/utils/date'

import { CustomRowTable } from './styles'

type Props = {
  dataSource: Machine[] | undefined
  getMachinesLoading: boolean
  onDelete: (id: Machine['id']) => void
}

const MachinesTable = ({ dataSource, getMachinesLoading, onDelete }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const getStatusTag = (isWorking: boolean) => {
    const status = isWorking
      ? {
          color: 'green',
          text: t('components.pages.machines.list.table.working.inService'),
          icon: <CheckCircleOutlined />,
        }
      : { color: 'red', text: t('components.pages.machines.list.table.working.idle'), icon: <CloseCircleOutlined /> }

    return (
      <Tag color={status.color}>
        {status.icon} {status.text}
      </Tag>
    )
  }

  const columns: ColumnsType<Machine> = [
    {
      title: t('components.pages.machines.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (record: Machine) => {
        return <CustomRowTable type="left">{record}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
    {
      title: t('components.pages.machines.list.table.typeName'),
      dataIndex: 'typeName',
      key: 'typeName',
      render: (typeName: string) => {
        return <CustomRowTable type="left">{typeName}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
    {
      title: t('components.pages.machines.list.table.status'),
      dataIndex: 'isWorking',
      key: 'isWorking',
      render: (isWorking: boolean) => {
        return <CustomRowTable type="left">{getStatusTag(isWorking)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
    {
      title: t('components.pages.machines.list.table.startedTime'),
      dataIndex: 'startedAt',
      key: 'startedAt',

      render: (startedAt: string) => {
        if (!startedAt) return <CustomRowTable type="left" />
        return <CustomRowTable type="left">{`${formatYMD(startedAt)} ${formatHm(startedAt)}`}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
    {
      title: t('components.pages.machines.list.table.finishedTime'),
      key: 'finishedTime',
      render: (record: Machine) => {
        if (!record?.startedAt) return <CustomRowTable type="left" />
        const finishedTime = moment(record.startedAt).add(record.laundryTime, 'minutes').format('YYYY/MM/DD HH:mm')
        return <CustomRowTable type="left">{finishedTime}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (text: string, record: Machine) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <Button size="small" onClick={() => goToDetailMachinePartPage(record.id)}>
                  {t('components.pages.machines.list.table.maintain')}
                </Button>
                <Button size="small" onClick={() => goToEditMachinePage(record.id)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const goToEditMachinePage = (id: Machine['id']) => {
    navigate(`${routes.machines.path}/${id}`)
  }

  const goToDetailMachinePartPage = (id: Machine['id']) => {
    const path = routes.machineParts.path.replace(':id', id.toString())
    navigate(path)
  }

  return (
    <Table
      rowKey="id"
      bordered
      dataSource={dataSource}
      columns={columns}
      loading={getMachinesLoading}
      pagination={false}
      scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
    />
  )
}

MachinesTable.displayName = 'MachinesTable'
export default MachinesTable
